import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatCpfCnpj } from "@/utils/cpfCnpjValidation";
import { validateForm, validateName, validateEmail, validateOptionalCpfCnpjField, validateOptionalPhoneField, validatePhone } from "@/utils/validators";
import { errorHandler } from "@/utils/errorHandler";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { useAuth } from "@/components/AuthProvider";

interface EditClienteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: any;
  onboardingProgress?: any;
  refetch?: () => Promise<void>;
}

interface TelefoneItem {
  id: string;
  numero: string;
  tipo: "celular" | "comercial" | "residencial" | "outro";
}

interface ContatoItem {
  id: string;
  nome: string;
  telefone: string;
  cargo: string;
}

export function EditClienteModal({
  open,
  onOpenChange,
  client,
  onboardingProgress,
  refetch,
}: EditClienteModalProps) {
  const queryClient = useQueryClient();
  const { empresa } = useAuth();

  const [tipoCliente, setTipoCliente] = useState<"pessoa_fisica" | "pessoa_juridica">("pessoa_fisica");
  const [telefones, setTelefones] = useState<TelefoneItem[]>([]);
  const [contatos, setContatos] = useState<ContatoItem[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    cpf_cnpj: "",
    email: "",
    data_nascimento: "",
    inscricao_estadual: "",
    inscricao_municipal: "",
    observacoes: "",
    endereco_logradouro: "",
    endereco_numero: "",
    endereco_complemento: "",
    endereco_bairro: "",
    endereco_cidade: "",
    endereco_uf: "",
    endereco_cep: "",
    endereco_entrega_logradouro: "",
    endereco_entrega_numero: "",
    endereco_entrega_complemento: "",
    endereco_entrega_bairro: "",
    endereco_entrega_cidade: "",
    endereco_entrega_uf: "",
    endereco_entrega_cep: "",
    endereco_cobranca_logradouro: "",
    endereco_cobranca_numero: "",
    endereco_cobranca_complemento: "",
    endereco_cobranca_bairro: "",
    endereco_cobranca_cidade: "",
    endereco_cobranca_uf: "",
    endereco_cobranca_cep: "",
  });

  const [usarEnderecoEntrega, setUsarEnderecoEntrega] = useState(false);
  const [usarEnderecoCobranca, setUsarEnderecoCobranca] = useState(false);
  const [novoTelefone, setNovoTelefone] = useState({ numero: "", tipo: "celular" as const });
  const [novoContato, setNovoContato] = useState({ nome: "", telefone: "", cargo: "" });
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  // Determinar abas baseado no tipo de cliente
  const getAbas = () => {
    const abas = ["basicos", "contatos", "enderecos", "observacoes"];
    if (tipoCliente === "pessoa_juridica") {
      abas.splice(2, 0, "empresa"); // Inserir "empresa" depois de "contatos"
    }
    return abas;
  };

  const abas = getAbas();
  const abaAtual = abas[currentTabIndex];
  const isUltimaAba = currentTabIndex === abas.length - 1;
  const isPrimeiraAba = currentTabIndex === 0;
  const canAddTelefone = !!novoTelefone.numero.trim() && validatePhone(novoTelefone.numero.trim()).isValid;

  const handleProximo = () => {
    if (abaAtual === "contatos" && novoTelefone.numero.trim()) {
      const telefoneAdicionado = handleAddTelefone();
      if (!telefoneAdicionado) {
        return;
      }
    }

    if (currentTabIndex < abas.length - 1) {
      setCurrentTabIndex(currentTabIndex + 1);
    }
  };

  const handleAnterior = () => {
    if (currentTabIndex > 0) {
      setCurrentTabIndex(currentTabIndex - 1);
    }
  };

  // Preencher formulário quando client muda
  useEffect(() => {
    if (!client) return;

    const legacyAddress = (() => {
      const parseAddressObject = (value: unknown) => {
        if (!value || typeof value !== "object") {
          return null;
        }

        const addressObject = value as Record<string, unknown>;
        return {
          logradouro: (addressObject.logradouro || addressObject.street || "").toString().trim(),
          numero: (addressObject.numero || addressObject.number || "").toString().trim(),
          complemento: (addressObject.complemento || addressObject.complement || "").toString().trim(),
          bairro: (addressObject.bairro || addressObject.neighborhood || "").toString().trim(),
          cidade: (addressObject.cidade || addressObject.city || "").toString().trim(),
          uf: (addressObject.uf || addressObject.state || "").toString().trim(),
          cep: (addressObject.cep || addressObject.zipcode || "").toString().trim(),
        };
      };

      const directObjectAddress = parseAddressObject(client?.address);
      if (directObjectAddress) {
        return directObjectAddress;
      }

      if (typeof client?.address !== "string" || !client.address.trim()) {
        return null;
      }

      const rawAddress = client.address.trim();

      try {
        const parsed = JSON.parse(rawAddress);
        const parsedObjectAddress = parseAddressObject(parsed);
        if (parsedObjectAddress) {
          return parsedObjectAddress;
        }
      } catch {
        // address legado em string livre, segue com fallback textual
      }

      const [logradouroRaw, numeroRaw] = rawAddress.split(",");

      return {
        logradouro: (logradouroRaw || rawAddress).trim(),
        numero: (numeroRaw || "").trim(),
        complemento: "",
        bairro: "",
        cidade: "",
        uf: "",
        cep: "",
      };
    })();

    setTipoCliente((client?.tipo_cliente as any) || "pessoa_fisica");
    setFormData({
      name: client?.name || "",
      cpf_cnpj: client?.cpf_cnpj || "",
      email: client?.email || "",
      data_nascimento: client?.data_nascimento ? client.data_nascimento.split('T')[0] : "",
      inscricao_estadual: client?.inscricao_estadual || "",
      inscricao_municipal: client?.inscricao_municipal || "",
      observacoes: client?.observacoes || "",
      endereco_logradouro: client?.endereco_logradouro || legacyAddress?.logradouro || "",
      endereco_numero: client?.endereco_numero || legacyAddress?.numero || "",
      endereco_complemento: client?.endereco_complemento || legacyAddress?.complemento || "",
      endereco_bairro: client?.endereco_bairro || legacyAddress?.bairro || "",
      endereco_cidade: client?.endereco_cidade || legacyAddress?.cidade || "",
      endereco_uf: client?.endereco_uf || legacyAddress?.uf || "",
      endereco_cep: client?.endereco_cep || legacyAddress?.cep || "",
      endereco_entrega_logradouro: client?.endereco_entrega_logradouro || "",
      endereco_entrega_numero: client?.endereco_entrega_numero || "",
      endereco_entrega_complemento: client?.endereco_entrega_complemento || "",
      endereco_entrega_bairro: client?.endereco_entrega_bairro || "",
      endereco_entrega_cidade: client?.endereco_entrega_cidade || "",
      endereco_entrega_uf: client?.endereco_entrega_uf || "",
      endereco_entrega_cep: client?.endereco_entrega_cep || "",
      endereco_cobranca_logradouro: client?.endereco_cobranca_logradouro || "",
      endereco_cobranca_numero: client?.endereco_cobranca_numero || "",
      endereco_cobranca_complemento: client?.endereco_cobranca_complemento || "",
      endereco_cobranca_bairro: client?.endereco_cobranca_bairro || "",
      endereco_cobranca_cidade: client?.endereco_cobranca_cidade || "",
      endereco_cobranca_uf: client?.endereco_cobranca_uf || "",
      endereco_cobranca_cep: client?.endereco_cobranca_cep || "",
    });

    // Carregar telefones (aceita array, JSON string e fallback para campo phone legado)
    const rawTelefones = (() => {
      if (Array.isArray(client?.telefones)) return client.telefones;
      if (typeof client?.telefones === "string" && client.telefones.trim()) {
        try {
          const parsed = JSON.parse(client.telefones);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          console.warn("Nao foi possivel fazer parse de client.telefones no EditClienteModal", client.telefones);
          return [];
        }
      }
      return [];
    })();

    const normalizedTelefones = rawTelefones
      .map((t: any, idx: number) => {
        if (typeof t === "string" && t.trim()) {
          return {
            id: `existing-${idx}`,
            numero: t.trim(),
            tipo: "celular" as const,
          };
        }

        if (t && typeof t === "object") {
          const numero = (t.numero || t.telefone || "").toString().trim();
          if (!numero) return null;
          return {
            id: `existing-${idx}`,
            numero,
            tipo: t.tipo || "celular",
          };
        }

        return null;
      })
      .filter((t): t is TelefoneItem => !!t);

    if (normalizedTelefones.length > 0) {
      setTelefones(normalizedTelefones);
    } else if (typeof client?.phone === "string" && client.phone.trim()) {
      setTelefones([{
        id: "existing-phone",
        numero: client.phone.trim(),
        tipo: "celular"
      }]);
    } else {
      setTelefones([]);
    }

    // Carregar contatos (PJ)
    if (client?.contatos && Array.isArray(client.contatos)) {
      setContatos(client.contatos.map((c: any, idx: number) => ({
        id: `existing-${idx}`,
        nome: c.nome || "",
        telefone: c.telefone || "",
        cargo: c.cargo || ""
      })));
    } else {
      setContatos([]);
    }

    // Verificar se tem endereço de entrega
    setUsarEnderecoEntrega(!!(client?.endereco_entrega_logradouro || client?.endereco_entrega_numero));

    // Verificar se tem endereço de cobrança
    setUsarEnderecoCobranca(!!(client?.endereco_cobranca_logradouro || client?.endereco_cobranca_numero));
  }, [client, open]);

  const handleAddTelefone = () => {
    const numeroTelefone = novoTelefone.numero.trim();

    if (!numeroTelefone) {
      toast.error("Digite um telefone");
      return false;
    }

    const phoneValidation = validatePhone(numeroTelefone);
    if (!phoneValidation.isValid) {
      phoneValidation.errors.forEach((error) => toast.error(error));
      return false;
    }

    setTelefones([...telefones, { id: Date.now().toString(), ...novoTelefone, numero: numeroTelefone }]);
    setNovoTelefone({ numero: "", tipo: "celular" });
    toast.success("Telefone adicionado");
    return true;
  };

  const handleRemoveTelefone = (id: string) => {
    setTelefones(telefones.filter(t => t.id !== id));
  };

  const handleAddContato = () => {
    if (!novoContato.nome.trim() || !novoContato.telefone.trim()) {
      toast.error("Preencha nome e telefone do contato");
      return;
    }
    setContatos([...contatos, { id: Date.now().toString(), ...novoContato }]);
    setNovoContato({ nome: "", telefone: "", cargo: "" });
  };

  const handleRemoveContato = (id: string) => {
    setContatos(contatos.filter(c => c.id !== id));
  };

  const handleSalvar = async () => {
    if (!client?.id) return;

    const firstTelefone = telefones.length > 0 ? telefones[0].numero.trim() : "";

    const validation = validateForm(
      { name: formData.name, phone: firstTelefone, email: formData.email, cpf_cnpj: formData.cpf_cnpj },
      {
        name: validateName,
        phone: validateOptionalPhoneField,
        email: (value) => (value && typeof value === "string" ? validateEmail(value) : { isValid: true, errors: [] }),
        cpf_cnpj: validateOptionalCpfCnpjField,
      }
    );

    if (!validation.isValid) {
      validation.errors.forEach((error) => toast.error(error));
      return;
    }

    try {
      // Para cliente de demo, simular sucesso
      if (client?.id.startsWith('demo-')) {
        toast.success(`Cliente "${formData.name}" atualizado com sucesso! (Modo demonstração)`);
        onOpenChange(false);
        return;
      }

      // Preparar dados para atualização
      const updateData: any = {
        name: formData.name.trim(),
        phone: firstTelefone || null,
        email: formData.email || null,
        tipo_cliente: tipoCliente,
        telefones: telefones.length > 0 ? JSON.stringify(telefones) : null,
        observacoes: formData.observacoes || null,
        data_nascimento: tipoCliente === "pessoa_fisica" && formData.data_nascimento ? formData.data_nascimento : null,
        cpf_cnpj: formData.cpf_cnpj ? formData.cpf_cnpj.replace(/[^\d]/g, "") : null,
        inscricao_estadual: formData.inscricao_estadual || null,
        inscricao_municipal: tipoCliente === "pessoa_juridica" ? formData.inscricao_municipal : null,
        contatos: tipoCliente === "pessoa_juridica" && contatos.length > 0 ? JSON.stringify(contatos) : null,
        endereco_logradouro: formData.endereco_logradouro || null,
        endereco_numero: formData.endereco_numero || null,
        endereco_complemento: formData.endereco_complemento || null,
        endereco_bairro: formData.endereco_bairro || null,
        endereco_cidade: formData.endereco_cidade || null,
        endereco_uf: formData.endereco_uf || null,
        endereco_cep: formData.endereco_cep || null,
      };

      // Endereço de entrega
      if (usarEnderecoEntrega) {
        updateData.endereco_entrega_logradouro = formData.endereco_entrega_logradouro || null;
        updateData.endereco_entrega_numero = formData.endereco_entrega_numero || null;
        updateData.endereco_entrega_complemento = formData.endereco_entrega_complemento || null;
        updateData.endereco_entrega_bairro = formData.endereco_entrega_bairro || null;
        updateData.endereco_entrega_cidade = formData.endereco_entrega_cidade || null;
        updateData.endereco_entrega_uf = formData.endereco_entrega_uf || null;
        updateData.endereco_entrega_cep = formData.endereco_entrega_cep || null;
      } else {
        updateData.endereco_entrega_logradouro = null;
        updateData.endereco_entrega_numero = null;
        updateData.endereco_entrega_complemento = null;
        updateData.endereco_entrega_bairro = null;
        updateData.endereco_entrega_cidade = null;
        updateData.endereco_entrega_uf = null;
        updateData.endereco_entrega_cep = null;
      }

      // Endereço de cobrança
      if (usarEnderecoCobranca) {
        updateData.endereco_cobranca_logradouro = formData.endereco_cobranca_logradouro || null;
        updateData.endereco_cobranca_numero = formData.endereco_cobranca_numero || null;
        updateData.endereco_cobranca_complemento = formData.endereco_cobranca_complemento || null;
        updateData.endereco_cobranca_bairro = formData.endereco_cobranca_bairro || null;
        updateData.endereco_cobranca_cidade = formData.endereco_cobranca_cidade || null;
        updateData.endereco_cobranca_uf = formData.endereco_cobranca_uf || null;
        updateData.endereco_cobranca_cep = formData.endereco_cobranca_cep || null;
      } else {
        updateData.endereco_cobranca_logradouro = null;
        updateData.endereco_cobranca_numero = null;
        updateData.endereco_cobranca_complemento = null;
        updateData.endereco_cobranca_bairro = null;
        updateData.endereco_cobranca_cidade = null;
        updateData.endereco_cobranca_uf = null;
        updateData.endereco_cobranca_cep = null;
      }

      // Atualizar no Supabase
      const { error } = await supabase
        .from("customers")
        .update(updateData)
        .eq("id", client.id);

      if (error) throw error;

      toast.success(`Cliente "${formData.name}" atualizado com sucesso!`);
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      await refetch?.();
      onOpenChange(false);
    } catch (error: any) {
      const appError = errorHandler.handleSupabaseError(error, 'editCustomer');
      toast.error(appError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>

        <Tabs value={abaAtual} onValueChange={() => {}} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${abas.length}, 1fr)` }}>
            {abas.map((aba) => (
              <TabsTrigger key={aba} value={aba} disabled>
                {aba === "basicos" && "Básicos"}
                {aba === "contatos" && "Contatos"}
                {aba === "empresa" && "Empresa"}
                {aba === "enderecos" && "Endereços"}
                {aba === "observacoes" && "Observações"}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ABA 1: BÁSICOS */}
          <TabsContent value="basicos" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Tipo de Cliente</Label>
              <select
                value={tipoCliente}
                onChange={(e) => setTipoCliente(e.target.value as any)}
                className="w-full h-10 px-3 rounded-lg border border-input bg-background"
              >
                <option value="pessoa_fisica">Pessoa Física</option>
                <option value="pessoa_juridica">Pessoa Jurídica</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  placeholder={tipoCliente === "pessoa_fisica" ? "Nome completo" : "Razão social"}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf_cnpj">
                  {tipoCliente === "pessoa_fisica" ? "CPF" : "CNPJ"} <span className="text-gray-400">(opcional)</span>
                </Label>
                <Input
                  id="cpf_cnpj"
                  placeholder={tipoCliente === "pessoa_fisica" ? "000.000.000-00" : "00.000.000/0000-00"}
                  value={formData.cpf_cnpj}
                  onChange={(e) => {
                    const formatted = formatCpfCnpj(e.target.value);
                    setFormData({ ...formData, cpf_cnpj: formatted });
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email <span className="text-gray-400">(opcional)</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="cliente@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              {tipoCliente === "pessoa_fisica" && (
                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de Nascimento <span className="text-gray-400">(opcional)</span></Label>
                  <Input
                    id="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                  />
                </div>
              )}

              {tipoCliente === "pessoa_juridica" && (
                <div className="space-y-2">
                  <Label htmlFor="ie">Inscrição Estadual <span className="text-gray-400">(opcional)</span></Label>
                  <Input
                    id="ie"
                    placeholder="Inscrição Estadual"
                    value={formData.inscricao_estadual}
                    onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* ABA 2: CONTATOS (TELEFONES) */}
          <TabsContent value="contatos" className="space-y-4 mt-4">
            <div className="space-y-3">
              <h3 className="font-semibold">Telefones <span className="text-gray-400 text-sm font-normal">(opcional)</span></h3>

              {telefones.map((tel) => (
                <div key={tel.id} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{tel.numero}</p>
                    <p className="text-xs text-gray-500 capitalize">{tel.tipo}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleRemoveTelefone(tel.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}

              <div className="space-y-2 p-3 border rounded-lg">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="(11) 99999-9999"
                    value={novoTelefone.numero}
                    onChange={(e) => setNovoTelefone({ ...novoTelefone, numero: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTelefone();
                      }
                    }}
                  />
                  <select
                    value={novoTelefone.tipo}
                    onChange={(e) => setNovoTelefone({ ...novoTelefone, tipo: e.target.value as any })}
                    className="h-10 px-3 rounded-lg border border-input bg-background text-sm"
                  >
                    <option value="celular">Celular</option>
                    <option value="comercial">Comercial</option>
                    <option value="residencial">Residencial</option>
                    <option value="outro">Outro</option>
                  </select>
                  <Button onClick={handleAddTelefone} disabled={!canAddTelefone} className="bg-gradient-to-r from-purple-600 to-pink-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar telefone
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Preencha e clique em <strong>Adicionar telefone</strong> para salvar na lista. Se você avançar com um telefone digitado, ele será adicionado automaticamente.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* ABA 3: EMPRESA (APENAS PJ) */}
          {tipoCliente === "pessoa_juridica" && (
            <TabsContent value="empresa" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="im">Inscrição Municipal <span className="text-gray-400">(opcional)</span></Label>
                <Input
                  id="im"
                  placeholder="Inscrição municipal"
                  value={formData.inscricao_municipal}
                  onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                />
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">Contatos da Empresa</h3>

                {contatos.map((contato) => (
                  <div key={contato.id} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{contato.nome}</p>
                      <p className="text-xs text-gray-500">{contato.telefone} • {contato.cargo}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveContato(contato.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}

                <div className="space-y-2 p-3 border rounded-lg">
                  <Input
                    placeholder="Nome do contato"
                    value={novoContato.nome}
                    onChange={(e) => setNovoContato({ ...novoContato, nome: e.target.value })}
                  />
                  <Input
                    placeholder="Telefone"
                    value={novoContato.telefone}
                    onChange={(e) => setNovoContato({ ...novoContato, telefone: e.target.value })}
                  />
                  <Input
                    placeholder="Cargo (ex: Gerente, Diretor)"
                    value={novoContato.cargo}
                    onChange={(e) => setNovoContato({ ...novoContato, cargo: e.target.value })}
                  />
                  <Button onClick={handleAddContato} className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Contato
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}

          {/* ABA 4: ENDEREÇOS */}
          <TabsContent value="enderecos" className="space-y-6 mt-4">
            {/* Endereço Padrão */}
            <div className="space-y-3">
              <h3 className="font-semibold">Endereço Padrão <span className="text-gray-400 text-sm font-normal">(opcional)</span></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Logradouro</Label>
                  <Input
                    placeholder="Rua, Avenida, etc."
                    value={formData.endereco_logradouro}
                    onChange={(e) => setFormData({ ...formData, endereco_logradouro: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input
                    placeholder="123"
                    value={formData.endereco_numero}
                    onChange={(e) => setFormData({ ...formData, endereco_numero: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input
                    placeholder="Apto, bloco, etc."
                    value={formData.endereco_complemento}
                    onChange={(e) => setFormData({ ...formData, endereco_complemento: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input
                    placeholder="Bairro"
                    value={formData.endereco_bairro}
                    onChange={(e) => setFormData({ ...formData, endereco_bairro: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    placeholder="Cidade"
                    value={formData.endereco_cidade}
                    onChange={(e) => setFormData({ ...formData, endereco_cidade: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>UF</Label>
                  <Input
                    placeholder="SP"
                    maxLength={2}
                    value={formData.endereco_uf}
                    onChange={(e) => setFormData({ ...formData, endereco_uf: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>CEP</Label>
                  <Input
                    placeholder="00000-000"
                    value={formData.endereco_cep}
                    onChange={(e) => setFormData({ ...formData, endereco_cep: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Endereço de Entrega */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="entrega"
                  checked={usarEnderecoEntrega}
                  onChange={(e) => setUsarEnderecoEntrega(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="entrega" className="font-semibold cursor-pointer">Usar endereço diferente para Entrega</Label>
              </div>

              {usarEnderecoEntrega && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Logradouro</Label>
                    <Input
                      placeholder="Rua, Avenida, etc."
                      value={formData.endereco_entrega_logradouro}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_logradouro: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número</Label>
                    <Input
                      placeholder="123"
                      value={formData.endereco_entrega_numero}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_numero: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Complemento</Label>
                    <Input
                      placeholder="Apto, bloco, etc."
                      value={formData.endereco_entrega_complemento}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_complemento: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bairro</Label>
                    <Input
                      placeholder="Bairro"
                      value={formData.endereco_entrega_bairro}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_bairro: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input
                      placeholder="Cidade"
                      value={formData.endereco_entrega_cidade}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_cidade: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>UF</Label>
                    <Input
                      placeholder="SP"
                      maxLength={2}
                      value={formData.endereco_entrega_uf}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_uf: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>CEP</Label>
                    <Input
                      placeholder="00000-000"
                      value={formData.endereco_entrega_cep}
                      onChange={(e) => setFormData({ ...formData, endereco_entrega_cep: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Endereço de Cobrança */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="cobranca"
                  checked={usarEnderecoCobranca}
                  onChange={(e) => setUsarEnderecoCobranca(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="cobranca" className="font-semibold cursor-pointer">Usar endereço diferente para Cobrança</Label>
              </div>

              {usarEnderecoCobranca && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label>Logradouro</Label>
                    <Input
                      placeholder="Rua, Avenida, etc."
                      value={formData.endereco_cobranca_logradouro}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_logradouro: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Número</Label>
                    <Input
                      placeholder="123"
                      value={formData.endereco_cobranca_numero}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_numero: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Complemento</Label>
                    <Input
                      placeholder="Apto, bloco, etc."
                      value={formData.endereco_cobranca_complemento}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_complemento: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bairro</Label>
                    <Input
                      placeholder="Bairro"
                      value={formData.endereco_cobranca_bairro}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_bairro: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input
                      placeholder="Cidade"
                      value={formData.endereco_cobranca_cidade}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_cidade: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>UF</Label>
                    <Input
                      placeholder="SP"
                      maxLength={2}
                      value={formData.endereco_cobranca_uf}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_uf: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>CEP</Label>
                    <Input
                      placeholder="00000-000"
                      value={formData.endereco_cobranca_cep}
                      onChange={(e) => setFormData({ ...formData, endereco_cobranca_cep: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ABA 5: OBSERVAÇÕES */}
          <TabsContent value="observacoes" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações <span className="text-gray-400">(opcional)</span></Label>
              <Textarea
                id="observacoes"
                placeholder="Adicione observações sobre o cliente..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={6}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
          </div>

          <div className="flex gap-2">
            {!isPrimeiraAba && (
              <Button variant="outline" onClick={handleAnterior}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}

            {!isUltimaAba ? (
              <Button onClick={handleProximo} className="bg-gradient-to-r from-purple-600 to-pink-600">
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSalvar} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                Salvar Alterações
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
